export const TTS_PROVIDERS = ["elevenlabs", "openai"] as const;

export type TtsProviderId = (typeof TTS_PROVIDERS)[number];

export const ELEVENLABS_MODEL_IDS = ["eleven_v3", "eleven_multilingual_v2"] as const;
export type ElevenLabsModelId = (typeof ELEVENLABS_MODEL_IDS)[number];

export const OPENAI_MODEL_IDS = ["gpt-4o-mini-tts", "tts-1-hd", "tts-1"] as const;
export type OpenAiModelId = (typeof OPENAI_MODEL_IDS)[number];

export interface TtsProviderOption {
  id: TtsProviderId;
  name: string;
  description: string;
}

export interface TtsVoiceOption {
  id: string;
  providerId: TtsProviderId;
  name: string;
  description: string;
}

export interface TtsModelOption {
  id: string;
  providerId: TtsProviderId;
  name: string;
  description: string;
  supportsInstructions?: boolean;
}

export interface ElevenLabsTtsSettings {
  voiceId: string;
  modelId: ElevenLabsModelId;
  stability: number;
  similarityBoost: number;
  style: number;
  speed: number;
  enhanceWithAudioTags: boolean;
}

export interface OpenAiTtsSettings {
  voiceId: string;
  modelId: OpenAiModelId;
  speed: number;
  instructions: string;
}

export interface TtsSettings {
  provider: TtsProviderId;
  simpleMode: boolean;
  followAlong: boolean;
  elevenlabs: ElevenLabsTtsSettings;
  openai: OpenAiTtsSettings;
}

export interface StoredTtsSettings {
  provider?: TtsProviderId;
  simpleMode?: boolean;
  followAlong?: boolean;
  elevenlabs?: Partial<ElevenLabsTtsSettings>;
  openai?: Partial<OpenAiTtsSettings>;
}

export interface TtsRequestSettings {
  text: string;
  lineOffset?: number;
  force?: boolean;
  settings?: StoredTtsSettings;
}

export type ActiveTtsConfig =
  | {
      provider: "elevenlabs";
      voiceId: string;
      modelId: ElevenLabsModelId;
      settings: ElevenLabsTtsSettings;
    }
  | {
      provider: "openai";
      voiceId: string;
      modelId: OpenAiModelId;
      settings: OpenAiTtsSettings;
    };

export const ELEVENLABS_SPEED_MIN = 0.7;
export const ELEVENLABS_SPEED_MAX = 1.2;
export const OPENAI_SPEED_MIN = 0.25;
export const OPENAI_SPEED_MAX = 4;
export const OPENAI_INSTRUCTIONS_MAX = 400;

export const TTS_PROVIDER_OPTIONS: TtsProviderOption[] = [
  {
    id: "elevenlabs",
    name: "ElevenLabs",
    description: "Expressive narration with deep voice controls.",
  },
  {
    id: "openai",
    name: "OpenAI",
    description: "Built-in voices with fast generation and instruction tuning.",
  },
];

export const TTS_MODEL_OPTIONS: TtsModelOption[] = [
  {
    id: "eleven_v3",
    providerId: "elevenlabs",
    name: "Eleven v3",
    description: "Highest expressiveness with optional AI audio tags.",
  },
  {
    id: "eleven_multilingual_v2",
    providerId: "elevenlabs",
    name: "Multilingual v2",
    description: "Classic ElevenLabs model with full voice controls.",
  },
  {
    id: "gpt-4o-mini-tts",
    providerId: "openai",
    name: "gpt-4o-mini-tts",
    description: "Newest OpenAI TTS model with instruction support.",
    supportsInstructions: true,
  },
  {
    id: "tts-1-hd",
    providerId: "openai",
    name: "tts-1-hd",
    description: "Higher-fidelity OpenAI speech output.",
  },
  {
    id: "tts-1",
    providerId: "openai",
    name: "tts-1",
    description: "Lower-latency OpenAI speech output.",
  },
];

export const TTS_VOICE_OPTIONS: TtsVoiceOption[] = [
  { id: "21m00Tcm4TlvDq8ikWAM", providerId: "elevenlabs", name: "Rachel", description: "Warm, calm narrator" },
  { id: "29vD33N1CtxCmqQRPOHJ", providerId: "elevenlabs", name: "Drew", description: "Well-rounded, confident" },
  { id: "2EiwWnXFnvU5JabPnv8n", providerId: "elevenlabs", name: "Clyde", description: "Deep, seasoned delivery" },
  { id: "5Q0t7uMcjvnagumLfvZi", providerId: "elevenlabs", name: "Paul", description: "Grounded and authoritative" },
  { id: "AZnzlk1XvdvUeBnXmlld", providerId: "elevenlabs", name: "Domi", description: "Strong and assertive" },
  { id: "CYw3kZ02Hs0563khs1Fj", providerId: "elevenlabs", name: "Dave", description: "Conversational and friendly" },
  { id: "D38z5RcWu1voky8WS1ja", providerId: "elevenlabs", name: "Fin", description: "Older Irish accent" },
  { id: "EXAVITQu4vr4xnSDxMaL", providerId: "elevenlabs", name: "Sarah", description: "Soft, young narrator" },
  { id: "ErXwobaYiN019PkySvjV", providerId: "elevenlabs", name: "Antoni", description: "Calm and even" },
  { id: "GBv7mTt0atIp3Br8iCZE", providerId: "elevenlabs", name: "Thomas", description: "Calm, collected" },
  { id: "IKne3meq5aSn9XLyUdCD", providerId: "elevenlabs", name: "Charlie", description: "Natural Australian" },
  { id: "JBFqnCBsd6RMkjVDRZzb", providerId: "elevenlabs", name: "George", description: "Warm British narrator" },
  { id: "N2lVS1w4EtoT3dr4eOWO", providerId: "elevenlabs", name: "Callum", description: "Intense transatlantic tone" },
  { id: "TX3LPaxmHKxFdv7VOQHJ", providerId: "elevenlabs", name: "Liam", description: "Articulate and authoritative" },
  { id: "XB0fDUnXU5powFXDhCwa", providerId: "elevenlabs", name: "Charlotte", description: "Calm and polished" },
  { id: "Xb7hH8MSUJpSbSDYk0k2", providerId: "elevenlabs", name: "Alice", description: "Confident British" },
  { id: "XrExE9yKIg1WjnnlVkGX", providerId: "elevenlabs", name: "Matilda", description: "Warm and friendly" },
  { id: "bIHbv24MWmeRgasZH58o", providerId: "elevenlabs", name: "Will", description: "Friendly, young voice" },
  { id: "iP95p4xoKVk53GoZ742B", providerId: "elevenlabs", name: "Chris", description: "Casual and conversational" },
  { id: "nPczCjzI2devNBz1zQrb", providerId: "elevenlabs", name: "Brian", description: "Deep and authoritative" },
  { id: "onwK4e9ZLuTAKqWW03F9", providerId: "elevenlabs", name: "Daniel", description: "Deep British narrator" },
  { id: "pFZP5JQG7iQjIQuC4Bku", providerId: "elevenlabs", name: "Lily", description: "Warm British narrator" },
  { id: "pqHfZKP75CvOlQylNhV4", providerId: "elevenlabs", name: "Bill", description: "Trustworthy American" },
  { id: "t0jbNlBVZ17f02VDIeMI", providerId: "elevenlabs", name: "Austin", description: "Warm, grounded narrator" },
  { id: "z9fAnlkpzviPz146aGWa", providerId: "elevenlabs", name: "Glinda", description: "Animated and theatrical" },
  { id: "alloy", providerId: "openai", name: "Alloy", description: "OpenAI voice" },
  { id: "ash", providerId: "openai", name: "Ash", description: "OpenAI voice" },
  { id: "ballad", providerId: "openai", name: "Ballad", description: "OpenAI voice" },
  { id: "coral", providerId: "openai", name: "Coral", description: "OpenAI voice" },
  { id: "echo", providerId: "openai", name: "Echo", description: "OpenAI voice" },
  { id: "fable", providerId: "openai", name: "Fable", description: "OpenAI voice" },
  { id: "onyx", providerId: "openai", name: "Onyx", description: "OpenAI voice" },
  { id: "nova", providerId: "openai", name: "Nova", description: "OpenAI voice" },
  { id: "sage", providerId: "openai", name: "Sage", description: "OpenAI voice" },
  { id: "shimmer", providerId: "openai", name: "Shimmer", description: "OpenAI voice" },
  { id: "verse", providerId: "openai", name: "Verse", description: "OpenAI voice" },
  { id: "marin", providerId: "openai", name: "Marin", description: "OpenAI recommended voice" },
  { id: "cedar", providerId: "openai", name: "Cedar", description: "OpenAI recommended voice" },
];

export const DEFAULT_TTS_SETTINGS: TtsSettings = {
  provider: "elevenlabs",
  simpleMode: false,
  followAlong: true,
  elevenlabs: {
    voiceId: "pqHfZKP75CvOlQylNhV4",
    modelId: "eleven_multilingual_v2",
    stability: 0.5,
    similarityBoost: 0.75,
    style: 0,
    speed: 1,
    enhanceWithAudioTags: true,
  },
  openai: {
    voiceId: "alloy",
    modelId: "gpt-4o-mini-tts",
    speed: 1,
    instructions: "",
  },
};

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function isProviderId(value: unknown): value is TtsProviderId {
  return typeof value === "string" && (TTS_PROVIDERS as readonly string[]).includes(value);
}

function isElevenLabsModelId(value: unknown): value is ElevenLabsModelId {
  return typeof value === "string" && (ELEVENLABS_MODEL_IDS as readonly string[]).includes(value);
}

function isOpenAiModelId(value: unknown): value is OpenAiModelId {
  return typeof value === "string" && (OPENAI_MODEL_IDS as readonly string[]).includes(value);
}

function getVoiceOption(providerId: TtsProviderId, voiceId: string | undefined): TtsVoiceOption | undefined {
  if (!voiceId) return undefined;
  return TTS_VOICE_OPTIONS.find((voice) => voice.providerId === providerId && voice.id === voiceId);
}

export function getProviderVoices(providerId: TtsProviderId): TtsVoiceOption[] {
  return TTS_VOICE_OPTIONS.filter((voice) => voice.providerId === providerId);
}

export function getProviderModels(providerId: TtsProviderId): TtsModelOption[] {
  return TTS_MODEL_OPTIONS.filter((model) => model.providerId === providerId);
}

export function getTtsProvider(providerId: TtsProviderId): TtsProviderOption {
  return TTS_PROVIDER_OPTIONS.find((provider) => provider.id === providerId) ?? TTS_PROVIDER_OPTIONS[0];
}

export function getCurrentTtsVoice(settings: TtsSettings): TtsVoiceOption {
  const active = getActiveTtsConfig(settings);
  return getVoiceOption(active.provider, active.voiceId)
    ?? getProviderVoices(active.provider)[0]
    ?? TTS_VOICE_OPTIONS[0];
}

export function getCurrentTtsModel(settings: TtsSettings): TtsModelOption {
  const active = getActiveTtsConfig(settings);
  return getProviderModels(active.provider).find((model) => model.id === active.modelId)
    ?? getProviderModels(active.provider)[0]
    ?? TTS_MODEL_OPTIONS[0];
}

export function getActiveTtsConfig(settings: TtsSettings): ActiveTtsConfig {
  if (settings.provider === "openai") {
    return {
      provider: "openai",
      voiceId: settings.openai.voiceId,
      modelId: settings.openai.modelId,
      settings: settings.openai,
    };
  }
  return {
    provider: "elevenlabs",
    voiceId: settings.elevenlabs.voiceId,
    modelId: settings.elevenlabs.modelId,
    settings: settings.elevenlabs,
  };
}

export function getTtsSettingsSignature(settings: TtsSettings): string {
  const active = getActiveTtsConfig(settings);
  return JSON.stringify({
    provider: active.provider,
    modelId: active.modelId,
    voiceId: active.voiceId,
    settings: active.settings,
  });
}

export function clampElevenLabsSettings(input: Partial<ElevenLabsTtsSettings> | undefined): ElevenLabsTtsSettings {
  const source = input ?? {};
  return {
    voiceId: getVoiceOption("elevenlabs", source.voiceId)?.id ?? DEFAULT_TTS_SETTINGS.elevenlabs.voiceId,
    modelId: isElevenLabsModelId(source.modelId) ? source.modelId : DEFAULT_TTS_SETTINGS.elevenlabs.modelId,
    stability: clamp(
      typeof source.stability === "number" ? source.stability : DEFAULT_TTS_SETTINGS.elevenlabs.stability,
      0,
      1
    ),
    similarityBoost: clamp(
      typeof source.similarityBoost === "number" ? source.similarityBoost : DEFAULT_TTS_SETTINGS.elevenlabs.similarityBoost,
      0,
      1
    ),
    style: clamp(
      typeof source.style === "number" ? source.style : DEFAULT_TTS_SETTINGS.elevenlabs.style,
      0,
      1
    ),
    speed: clamp(
      typeof source.speed === "number" ? source.speed : DEFAULT_TTS_SETTINGS.elevenlabs.speed,
      ELEVENLABS_SPEED_MIN,
      ELEVENLABS_SPEED_MAX
    ),
    enhanceWithAudioTags:
      typeof source.enhanceWithAudioTags === "boolean"
        ? source.enhanceWithAudioTags
        : DEFAULT_TTS_SETTINGS.elevenlabs.enhanceWithAudioTags,
  };
}

export function clampOpenAiSettings(input: Partial<OpenAiTtsSettings> | undefined): OpenAiTtsSettings {
  const source = input ?? {};
  return {
    voiceId: getVoiceOption("openai", source.voiceId)?.id ?? DEFAULT_TTS_SETTINGS.openai.voiceId,
    modelId: isOpenAiModelId(source.modelId) ? source.modelId : DEFAULT_TTS_SETTINGS.openai.modelId,
    speed: clamp(
      typeof source.speed === "number" ? source.speed : DEFAULT_TTS_SETTINGS.openai.speed,
      OPENAI_SPEED_MIN,
      OPENAI_SPEED_MAX
    ),
    instructions:
      typeof source.instructions === "string"
        ? source.instructions.slice(0, OPENAI_INSTRUCTIONS_MAX)
        : DEFAULT_TTS_SETTINGS.openai.instructions,
  };
}

export function resolveTtsSettings(input?: StoredTtsSettings | null): TtsSettings {
  return {
    provider: isProviderId(input?.provider) ? input.provider : DEFAULT_TTS_SETTINGS.provider,
    simpleMode:
      typeof input?.simpleMode === "boolean" ? input.simpleMode : DEFAULT_TTS_SETTINGS.simpleMode,
    followAlong:
      typeof input?.followAlong === "boolean" ? input.followAlong : DEFAULT_TTS_SETTINGS.followAlong,
    elevenlabs: clampElevenLabsSettings(input?.elevenlabs),
    openai: clampOpenAiSettings(input?.openai),
  };
}

